// Тренировка в зале (/workout): открыть по плану, из шаблона, с ИИ или пустую — сначала она
// только подготовлена (смотреть и править), идёт с «Старт»; подходы одним тапом, таймер отдыха,
// работа без связи. Тренировка живёт в IndexedDB и уходит на сервер очередью.

import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { api, errorMessage } from '@/api/client'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button, Card, EmptyState, ErrorText, Modal, PageSpinner, Spinner, useToast } from '@/components/ui'
import { formatTime, pluralRu } from '@/lib/format'
import { todayIso } from '@/lib/periods'
import { setActiveWorkout, useActiveSession, useActiveWorkout, type ActiveWorkout } from '@/offline/activeSession'
import { useOnline } from '@/offline/queue'
import { ExercisePicker } from '@/pages/exercises/ExercisePicker'
import { useCalendar, type Planned } from '@/pages/plan/api'
import { trainingApi, useTemplates, type Exercise, type Template } from '@/pages/workouts/api'

import type { EditOperation, WorkoutDraft } from '@/pages/ai/api'
import { AiEditSheet } from '@/pages/ai/EditChat'

import {
  addExercise,
  applyAiEdit,
  cancelDraft,
  discardWorkout,
  finishWorkout,
  moveExercise,
  prepareWorkout,
  removeExercise,
  replaceExercise,
  startWorkout,
} from './actions'
import { AiCheckCard } from './AiCheck'
import { AiWorkoutCard } from './AiWorkout'
import { cachedTemplates, findPlanned, loadLast, loadPrescription } from './data'
import { useOpenExercises } from './layout'
import {
  buildExercise,
  doneCount,
  emptyWorkout,
  nextPosition,
  replacementTargets,
  toEditItems,
  workoutFromAi,
  workoutFromPrescription,
  workoutFromSession,
  workoutFromTemplate,
} from './logic'
import { ExerciseCard, RestTimerBar, SetTimerBar, SyncStatus, WorkoutHead } from './parts'

type StartChoice =
  | { kind: 'empty' }
  | { kind: 'template'; template: Template }
  | { kind: 'planned'; planned: Planned }
  | { kind: 'ai'; draft: WorkoutDraft }

/** Собрать тренировку по выбору и подготовить её (без старта). */
function useStarter() {
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = async (choice: StartChoice) => {
    setBusy(true)
    setError(null)
    try {
      let workout: ActiveWorkout
      if (choice.kind === 'empty') {
        workout = emptyWorkout()
      } else if (choice.kind === 'ai') {
        const ids = choice.draft.exercises.flatMap((e) => (e.exercise_id ? [e.exercise_id] : []))
        workout = workoutFromAi(choice.draft, await loadLast(qc, ids))
      } else if (choice.kind === 'template') {
        const last = await loadLast(qc, choice.template.exercises.map((e) => e.exercise_id))
        workout = workoutFromTemplate(choice.template, last)
      } else {
        const { planned } = choice
        const templateId = planned.template_id
        if (!templateId) throw new Error('У плановой тренировки нет шаблона')
        const prescription = await loadPrescription(qc, templateId, planned.day)
        if (prescription) {
          const last = await loadLast(qc, prescription.exercises.map((e) => e.exercise_id))
          workout = workoutFromPrescription(prescription, planned.id, last)
        } else {
          // Целей плана нет в кэше (офлайн) — берём цели шаблона.
          const template = cachedTemplates(qc).find((t) => t.id === templateId)
          if (!template) throw new Error('Нет связи, а план на сегодня ещё не загружен')
          const last = await loadLast(qc, template.exercises.map((e) => e.exercise_id))
          workout = { ...workoutFromTemplate(template, last), planned_session_id: planned.id }
        }
      }
      await prepareWorkout(workout)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось открыть тренировку')
    } finally {
      setBusy(false)
    }
  }
  return { start, busy, error }
}

function ResumeServer() {
  const { session } = useActiveSession()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  if (!session || session.source !== 'server') return null
  const resume = async () => {
    setBusy(true)
    setError(null)
    try {
      const full = await trainingApi.session(session.id)
      await setActiveWorkout(workoutFromSession(full))
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Card title="Незавершённая тренировка" subtitle={`${session.title}, начата в ${formatTime(session.started_at)}`}>
      <Button variant="primary" size="lg" block loading={busy} onClick={() => void resume()}>
        Продолжить
      </Button>
      <ErrorText error={error} className="mt-2" />
    </Card>
  )
}

function StartView() {
  const [params, setParams] = useSearchParams()
  const qc = useQueryClient()
  const online = useOnline()
  const today = todayIso()
  const calendar = useCalendar(today, today)
  const templates = useTemplates(false)
  const { start, busy, error } = useStarter()
  const [planError, setPlanError] = useState<string | null>(null)

  // /workout?planned=<id> — кнопка «Открыть» из плана: сразу готовим её (без старта).
  const plannedParam = params.get('planned')
  const autoStarted = useRef<string | null>(null)
  useEffect(() => {
    if (!plannedParam || autoStarted.current === plannedParam) return
    autoStarted.current = plannedParam
    void findPlanned(qc, plannedParam).then((planned) => {
      setParams({}, { replace: true })
      if (planned?.template_id) void start({ kind: 'planned', planned })
      else setPlanError('Плановая тренировка не найдена')
    })
    // start меняется каждый рендер; запуск нужен один раз на id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannedParam, qc, setParams])

  const planned = (calendar.data ?? []).filter(
    (p) => p.kind === 'strength' && p.template_id && (p.status === 'planned' || p.status === 'missed'),
  )

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Тренировка"
        description="Открой по плану, из шаблона, с ИИ или пустую — начнётся по кнопке «Старт»"
      />
      {busy ? (
        <div role="status" className="flex items-center gap-2 text-sm text-muted">
          <Spinner size={16} /> Готовлю подходы…
        </div>
      ) : null}
      <ErrorText>{error ?? planError}</ErrorText>
      <ResumeServer />
      {planned.length > 0 ? (
        <Card title="По плану на сегодня">
          <div className="flex flex-col gap-2">
            {planned.map((p) => (
              <Button
                key={p.id}
                variant="primary"
                size="lg"
                block
                disabled={busy}
                onClick={() => void start({ kind: 'planned', planned: p })}
              >
                {p.template_name ?? 'Тренировка'}
              </Button>
            ))}
          </div>
        </Card>
      ) : null}
      <Card title="Из шаблона">
        {templates.isPending && online ? (
          <Spinner size={18} />
        ) : (templates.data ?? []).length === 0 ? (
          <p className="text-sm text-muted">{online ? 'Шаблонов пока нет.' : 'Шаблоны не загружены — нет связи.'}</p>
        ) : (
          <div className="flex flex-col gap-2">
            {(templates.data ?? []).map((t) => (
              <Button
                key={t.id}
                size="lg"
                block
                className="justify-between"
                disabled={busy}
                onClick={() => void start({ kind: 'template', template: t })}
              >
                <span className="min-w-0 truncate">{t.name}</span>
                <span className="shrink-0 text-sm font-normal text-muted">
                  {t.exercises.length} {pluralRu(t.exercises.length, ['упражнение', 'упражнения', 'упражнений'])}
                </span>
              </Button>
            ))}
          </div>
        )}
      </Card>
      {online ? <AiWorkoutCard busy={busy} onStart={(draft) => void start({ kind: 'ai', draft })} /> : null}
      <Button size="lg" block icon="plus" disabled={busy} onClick={() => void start({ kind: 'empty' })}>
        Пустая тренировка
      </Button>
    </div>
  )
}

/** Высота липкой шапки тренировки — под ней липнет заголовок раскрытого упражнения. */
function useHeadHeight(): number {
  const [height, setHeight] = useState(0)
  useEffect(() => {
    const head = document.querySelector<HTMLElement>('[data-workout-head]')
    if (!head || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => setHeight(head.getBoundingClientRect().height))
    observer.observe(head)
    return () => observer.disconnect()
  }, [])
  return height
}

function ActiveView({ workout }: { workout: ActiveWorkout }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const toast = useToast()
  const online = useOnline()
  const [picker, setPicker] = useState(false)
  const [ai, setAi] = useState(false)
  const [confirm, setConfirm] = useState<null | 'finish' | 'discard' | { removeKey: string; name: string }>(null)
  const [adding, setAdding] = useState(false)
  const done = doneCount(workout)
  const draft = Boolean(workout.draft)
  const [starting, setStarting] = useState(false)
  const { open, toggle, expand } = useOpenExercises(workout.id)
  const headHeight = useHeadHeight()

  const pick = async (exercise: Exercise) => {
    setPicker(false)
    setAdding(true)
    try {
      const last = await loadLast(qc, [exercise.id])
      const added = buildExercise({
        exercise_id: exercise.id,
        name: exercise.name,
        tracking: exercise.tracking,
        position: nextPosition(workout),
        targets: [],
        last: last(exercise.id),
      })
      await addExercise(added)
      expand(added.key)
    } finally {
      setAdding(false)
    }
  }

  // Замена на месте: подходов столько же, вес — из прошлого раза с новым упражнением.
  const replace = async (old: ActiveWorkout['exercises'][number], exercise: Exercise) => {
    const last = await loadLast(qc, [exercise.id])
    const replacement = buildExercise({
      exercise_id: exercise.id,
      name: exercise.name,
      tracking: exercise.tracking,
      position: old.position,
      targets: replacementTargets(old, exercise.tracking),
      last: last(exercise.id),
    })
    // Раскрыть заранее, чтобы новое упражнение не мелькнуло свёрнутым.
    expand(replacement.key)
    await replaceExercise(old.key, replacement)
  }

  // Правки ИИ: веса для новых упражнений — из прошлого раза; добавленные раскрыты.
  const applyAi = async (operations: EditOperation[]) => {
    const ids = operations.flatMap((o) => (o.exercise?.exercise_id ? [o.exercise.exercise_id] : []))
    const added = await applyAiEdit(operations, await loadLast(qc, ids))
    for (const key of added) expand(key)
  }

  const finish = async () => {
    setConfirm(null)
    const id = await finishWorkout()
    if (!id) return
    if (online) {
      navigate(`/workouts/${id}`, { state: { celebrate: true } })
    } else {
      toast.show({ message: 'Тренировка завершена. Уйдёт на сервер, когда появится связь' })
    }
  }

  const start = async () => {
    setStarting(true)
    try {
      await startWorkout()
    } finally {
      setStarting(false)
    }
  }

  const discard = async () => {
    setConfirm(null)
    const result = await discardWorkout()
    if (!result?.reachedServer) return
    try {
      await api.delete(`/sessions/${result.id}`)
    } catch (e) {
      toast.show({
        tone: 'danger',
        message: `${errorMessage(e, 'Не удалось удалить')}: тренировка осталась в истории — удали её там`,
      })
    }
  }

  return (
    // Запас снизу — чтобы и последний подход можно было поднять под шапку.
    <div
      className="flex flex-col gap-3 pb-[max(7rem,45dvh)]"
      style={{ '--workout-head-h': `${headHeight}px` } as CSSProperties}
    >
      <WorkoutHead workout={workout} />
      {draft ? (
        <p className="text-sm text-muted">Посмотри и поправь упражнения — секундомер пойдёт после «Старт».</p>
      ) : (
        <SyncStatus />
      )}
      {workout.exercises.length === 0 ? (
        <EmptyState icon="workout" title="Упражнений пока нет" description="Добавь первое — подходы появятся сами." />
      ) : null}
      {workout.exercises.map((exercise, index) => (
        <ExerciseCard
          key={exercise.key}
          exercise={exercise}
          index={index}
          total={workout.exercises.length}
          draft={draft}
          open={open.has(exercise.key)}
          onToggle={() => toggle(exercise.key)}
          onMove={(delta) => void moveExercise(exercise.key, delta)}
          onReplace={(picked) => void replace(exercise, picked)}
          onRemove={() => setConfirm({ removeKey: exercise.key, name: exercise.name })}
        />
      ))}
      <div className="flex gap-2">
        <Button size="lg" icon="plus" className="flex-1" loading={adding} onClick={() => setPicker(true)}>
          Добавить упражнение
        </Button>
        {online ? (
          <Button size="lg" icon="ai" onClick={() => setAi(true)}>
            С ИИ
          </Button>
        ) : null}
      </div>
      {draft ? (
        <Button variant="ghost" className="self-center text-danger" onClick={() => void cancelDraft()}>
          Отменить
        </Button>
      ) : (
        <>
          {online ? <AiCheckCard workout={workout} /> : null}
          <Button size="lg" block variant="primary" icon="check" onClick={() => setConfirm('finish')}>
            Завершить тренировку
          </Button>
          <Button variant="ghost" className="self-center text-danger" onClick={() => setConfirm('discard')}>
            Бросить без сохранения
          </Button>
        </>
      )}

      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 px-3 lg:bottom-4 lg:left-auto lg:right-6 lg:w-96">
        {draft ? (
          <Button
            size="lg"
            block
            variant="primary"
            icon="play"
            loading={starting}
            className="pointer-events-auto mx-auto max-w-xl shadow-lg shadow-black/30"
            onClick={() => void start()}
          >
            Старт
          </Button>
        ) : (
          <>
            <SetTimerBar className="pointer-events-auto mx-auto max-w-xl" />
            <RestTimerBar className="pointer-events-auto mx-auto max-w-xl" />
          </>
        )}
      </div>

      <ExercisePicker open={picker} onClose={() => setPicker(false)} onPick={(e) => void pick(e)} />
      <AiEditSheet
        open={ai}
        onClose={() => setAi(false)}
        mode="workout"
        title={workout.template_name}
        items={toEditItems(workout)}
        onApply={applyAi}
      />

      <Modal
        open={confirm === 'finish'}
        onClose={() => setConfirm(null)}
        title="Завершить тренировку?"
        description={
          done === 0
            ? 'Ни одного выполненного подхода — тренировка сохранится пустой.'
            : `${done} ${pluralRu(done, ['подход', 'подхода', 'подходов'])} выполнено.`
        }
        footer={
          <>
            <Button onClick={() => setConfirm(null)}>Продолжить</Button>
            <Button variant="primary" onClick={() => void finish()}>
              Завершить
            </Button>
          </>
        }
      />
      <Modal
        open={confirm === 'discard'}
        onClose={() => setConfirm(null)}
        title="Бросить тренировку?"
        description="Тренировка и все её подходы будут удалены."
        footer={
          <>
            <Button onClick={() => setConfirm(null)}>Отмена</Button>
            <Button variant="danger" onClick={() => void discard()}>
              Бросить
            </Button>
          </>
        }
      />
      <Modal
        open={typeof confirm === 'object' && confirm !== null}
        onClose={() => setConfirm(null)}
        title="Убрать упражнение?"
        description={typeof confirm === 'object' && confirm ? `«${confirm.name}» и его подходы уйдут из тренировки.` : undefined}
        footer={
          <>
            <Button onClick={() => setConfirm(null)}>Отмена</Button>
            <Button
              variant="danger"
              onClick={() => {
                if (typeof confirm === 'object' && confirm) void removeExercise(confirm.removeKey)
                setConfirm(null)
              }}
            >
              Убрать
            </Button>
          </>
        }
      />
    </div>
  )
}

export function WorkoutPage() {
  const { workout, ready } = useActiveWorkout()
  if (!ready) return <PageSpinner />
  return workout ? <ActiveView workout={workout} /> : <StartView />
}
